import {
  CreateReducer,
  CreateAction,
} from '../../store/utils/store'

import {
  IQuery_Org_Scope,
  IQuery_Org_Id,
  IRecord_WithMaybeTags,
} from '@kaidam/fc-shared/src/types/utils'

import {
  getProjectHandlers,
} from '../../store/utils/api'

import {
  CRMState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  IRecord_CRMTag,
  IRecord_CRMTag_Core,
  IRecord_CRMTag_Update,
  IRecord_CRMImport_WithJobsAndUser,
  IRecord_CRMImport_Fixed,
  IRecord_CRMContact_Core,
  IRecord_CRMContact_WithTags,
  IRecord_CRMContact_WithData,
  IRecord_CRMContact_Update,
  IData_CRMEvent_Type_Summary,
  IData_CRMEvent_Group_Summary,
  IRecord_CRMEvent,
  IRecord_CRMEvent_Core,
  IRecord_CRMEvent_With_Data,
  IRecord_CRMNote,
  IRecord_CRMNote_Core,
  IData_CRMEvent_Notes,
} from '@kaidam/fc-shared/src/types/schema/crm'

import {
  IElasticSearchResult,
} from '@kaidam/fc-shared/src/types/elasticsearch'

import {
  IData_Paginated_Query_Wrapper,
  IData_Paginated_Results_Wrapper,
} from '@kaidam/fc-shared/src/types/pagination'

import {
  IData_MagicTag_Summary,
} from '@kaidam/fc-shared/src/types/schema/magic_tag'

import {
  IElasticSearch_Source_CRM,
  IData_CRMUpdateMappings,
  IQuery_CRMEvent_List,
  IQuery_CRMContact_List,
  IQuery_CRM_Summary,
  IData_CRM_Summary,
} from '@kaidam/fc-shared/src/types/crm'

import {
  PayloadSearch,
} from '../../types/crm'

import {
  IPubsub_Message_Type_Crm_Import_Event,
  IPubsub_Message_Body_Crm_Import_Event
} from '@kaidam/fc-shared/src/types/pubsub'

import {
  UpdatePayloadIdOrgEntity,
  UpdatePayloadIdOrg,
  UpdatePayloadOrg,
  UpdatePayloadId,
  PayloadIdOrg,
} from '../../types/store'

import {
  NetworkThunk,
} from '../utils/network'

import {
  getSocketClientFactory,
} from '../../store/utils/websocket'

import {
  crmImportChannel,
} from '@kaidam/fc-shared/src/pubsub/channels'

import {
  getPaginationQueryParams,
} from '@kaidam/fc-shared/src/utils/pagination'

import {
  stripObjectValues,
} from '@kaidam/fc-shared/src/utils/object'

import projectSelectors from '../selectors/project'

import snackbarActions from './snackbar'

import {
  PAGINATION_RESULTS_DEFAULT,
  ROLE_SCOPE_DESCENDENTS,
} from '@kaidam/fc-shared/src/constants'

const getImportUpdateSocket = getSocketClientFactory<
  IPubsub_Message_Type_Crm_Import_Event,
  IPubsub_Message_Body_Crm_Import_Event
>()

const prefix = 'crm'

/*

  initial state

*/
const initialState: CRMState = {
  tags: [],
  imports: [],
  contactData: [],
  contactDetail: null,
  contactPagination: PAGINATION_RESULTS_DEFAULT,
  searchResults: [],
  eventTypes: [],
  eventGroups: [],
  eventData: [],
  eventPagination: PAGINATION_RESULTS_DEFAULT,
  magicTags: [],
  querySummary: [],
}

/*

  reducers

*/
const setTags: ReducerHandler<CRMState, IRecord_CRMTag[]> = (state, action) => {
  state.tags = action.payload
}

const setImports: ReducerHandler<CRMState, IRecord_CRMImport_WithJobsAndUser[]> = (state, action) => {
  state.imports = action.payload
}

const setSearchResults: ReducerHandler<CRMState, IElasticSearchResult<IElasticSearch_Source_CRM>[]> = (state, action) => {
  state.searchResults = action.payload
}

const setEventTypes: ReducerHandler<CRMState, IData_CRMEvent_Type_Summary[]> = (state, action) => {
  state.eventTypes = action.payload
}

const setEventGroups: ReducerHandler<CRMState, IData_CRMEvent_Group_Summary[]> = (state, action) => {
  state.eventGroups = action.payload
}

const setEventData: ReducerHandler<CRMState, IData_Paginated_Results_Wrapper<IRecord_CRMEvent_With_Data>> = (state, action) => {
  const {
    data,
    pagination,
  } = action.payload
  state.eventData = data
  state.eventPagination = pagination
}

const setContactData: ReducerHandler<CRMState, IData_Paginated_Results_Wrapper<IRecord_CRMContact_WithTags>> = (state, action) => {
  const {
    data,
    pagination,
  } = action.payload
  state.contactData = data
  state.contactPagination = pagination
}

const resetContactData: ReducerHandler<CRMState, void> = (state, action) => {
  state.contactData = []
  state.contactPagination = PAGINATION_RESULTS_DEFAULT
}


const setContactDetail: ReducerHandler<CRMState, IRecord_CRMContact_WithData> = (state, action) => {
  state.contactDetail = action.payload
}

const setContactDetailNotes: ReducerHandler<CRMState, IRecord_CRMContact_WithData> = (state, action) => {
  if(!state.contactDetail) return
  state.contactDetail.notes = action.payload.notes
}

const setContactDetailEvents: ReducerHandler<CRMState, IRecord_CRMContact_WithData> = (state, action) => {
  if(!state.contactDetail) return
  state.contactDetail.events = action.payload.events
}

const setMagicTags: ReducerHandler<CRMState, IData_MagicTag_Summary[]> = (state, action) => {
  state.magicTags = action.payload
}

const setQuerySummary: ReducerHandler<CRMState, IData_CRM_Summary[]> = (state, action) => {
  state.querySummary = action.payload
}

/*

  tags

*/
const listTags: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_CRMTag[]>(`/crm/tags`)
  dispatch(actions.setTags(data))
}

const createTag: ThunkHandler<IRecord_CRMTag_Core, boolean> = (data) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.post<IRecord_CRMTag_Core, IRecord_CRMTag>(`/crm/tags`, data)
  dispatch(listTags())
  dispatch(snackbarActions.setSuccess(`tag created`))
  return true
}

const updateTag: ThunkHandler<UpdatePayloadIdOrg<IRecord_CRMTag_Update>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put<IRecord_CRMTag_Update, IRecord_CRMTag>(`/crm/tags/${payload.org}/${payload.id}`, payload.data)
  dispatch(listTags())
  dispatch(snackbarActions.setSuccess(`tag updated`))
  return true
}

const deleteTag: ThunkHandler<PayloadIdOrg, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete<IRecord_CRMTag>(`/crm/tags/${payload.org}/${payload.id}`)
  dispatch(listTags())
  dispatch(snackbarActions.setSuccess(`tag deleted`))
  return true
}

/*

  imports

*/

const listImports: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_CRMImport_WithJobsAndUser[]>(`/crm/imports`)
  dispatch(actions.setImports(data))
}

const createImport: ThunkHandler<IRecord_CRMImport_Fixed, IRecord_CRMImport_WithJobsAndUser> = (data) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const result = await handlers.post<IRecord_CRMImport_Fixed, IRecord_CRMImport_WithJobsAndUser>(`/crm/imports`, data)
  await dispatch(listImports())
  return result
}

const processImport: ThunkHandler<UpdatePayloadId<IData_CRMUpdateMappings>, IRecord_CRMImport_WithJobsAndUser> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const result = await handlers.put<IData_CRMUpdateMappings, IRecord_CRMImport_WithJobsAndUser>(`/crm/imports/${payload.id}/process`, payload.data)
  await dispatch(listImports())
  return result
}

const runImport: ThunkHandler<string, IRecord_CRMImport_WithJobsAndUser> = (id) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const result = await handlers.put<void, IRecord_CRMImport_WithJobsAndUser>(`/crm/imports/${id}/run`)
  await dispatch(listImports())
  return result
}

const subscribeToImportUpdates: ThunkHandler<void, boolean> = () => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = crmImportChannel.wildcard(project)
  const socket = getImportUpdateSocket()
  await socket.subscribe(channel, async (message) => {
    dispatch(listImports())
  })
  return true
}

const unsubscribeToImportUpdates: ThunkHandler<void, boolean> = () => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = crmImportChannel.wildcard(project)
  const socket = getImportUpdateSocket()
  socket.unsubscribe(channel)
  return true
}

/*

  events

*/
const loadEventTypes: ThunkHandler<IQuery_Org_Scope, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_CRMEvent_Type_Summary[]>(`/crm/event_types/${query.org}/${query.scope}`)
  dispatch(actions.setEventTypes(data))
}

const loadEventGroups: ThunkHandler<IQuery_Org_Scope, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_CRMEvent_Group_Summary[]>(`/crm/event_groups/${query.org}/${query.scope}`)
  dispatch(actions.setEventGroups(data))
}

const loadEvents: ThunkHandler<IData_Paginated_Query_Wrapper<IQuery_CRMEvent_List & IQuery_Org_Scope>, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const {
    pagination,
    query,
  } = payload
  const paginationQueryParams = getPaginationQueryParams(pagination)
  const [searchQueryParams, [org, scope]] = stripObjectValues(query, ['org', 'scope'])
  if(!org) throw new Error(`org required for loadEvents`)
  if(!scope) throw new Error(`scope required for loadEvents`)
  const allQueryParams = Object.assign({}, paginationQueryParams, searchQueryParams)
  const data = await handlers.get<IData_Paginated_Results_Wrapper<IRecord_CRMEvent_With_Data>>(`/crm/events/${org}/${scope}`, {
    params: allQueryParams,
  })
  dispatch(actions.setEventData(data))
}

/*

  contacts

*/
const search: ThunkHandler<PayloadSearch, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IElasticSearchResult<IElasticSearch_Source_CRM>[]>(`/crm/search/${payload.org}`, {
    params: {
      search: payload.query,
      scope: ROLE_SCOPE_DESCENDENTS,
    }
  })
  dispatch(actions.setSearchResults(data))
}

const loadContacts: ThunkHandler<IData_Paginated_Query_Wrapper<IQuery_CRMContact_List & IQuery_Org_Scope>, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const {
    pagination,
    query,
  } = payload
  const paginationQueryParams = getPaginationQueryParams(pagination)

  const tags: string[] | undefined = query.tags
  const [searchQueryParams, [org, scope]] = stripObjectValues(query, ['org', 'scope', 'tags'])
  if(!org) throw new Error(`org required for loadContacts`)
  if(!scope) throw new Error(`scope required for loadContacts`)
  const allQueryParams = Object.assign({}, paginationQueryParams, searchQueryParams)

  if(tags) {
    tags.forEach(tag => {
      allQueryParams[tag] = 'yes'
    })
  }

  const data = await handlers.get<IData_Paginated_Results_Wrapper<IRecord_CRMContact_WithTags>>(`/crm/contacts/${org}/${scope}`, {
    params: allQueryParams,
  })
  dispatch(actions.setContactData(data))
}

const getContact: ThunkHandler<IQuery_Org_Id, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_CRMContact_WithData>(`/crm/contacts/${query.org}/${query.id}/data`)
  dispatch(actions.setContactDetail(data))
}

const createContact: ThunkHandler<UpdatePayloadOrg<IRecord_CRMContact_Core>, IRecord_CRMContact_WithData> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState, false)
  const result = await handlers.post<IRecord_CRMContact_Core, IRecord_CRMContact_WithData>(`/crm/contacts/${payload.org}`, payload.data)
  await dispatch(getContact({
    org: payload.org,
    id: result.id,
  }))
  dispatch(snackbarActions.setSuccess(`contact created`))
  return result
}

const updateContact: ThunkHandler<UpdatePayloadIdOrg<IRecord_CRMContact_Update & IRecord_WithMaybeTags>, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put<IRecord_CRMContact_Update & IRecord_WithMaybeTags, IRecord_CRMContact_WithData>(`/crm/contacts/${query.org}/${query.id}`, query.data)
  await dispatch(getContact({
    org: query.org,
    id: query.id,
  }))
  dispatch(snackbarActions.setSuccess(`contact updated`))
  return true
}

const deleteContact: ThunkHandler<UpdatePayloadIdOrg, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete<IRecord_CRMContact_WithData>(`/crm/contacts/${query.org}/${query.id}`)
  dispatch(snackbarActions.setSuccess(`contact deleted`))
}

/*

  notes

*/

const createNote: ThunkHandler<UpdatePayloadIdOrg<IRecord_CRMNote_Core>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.post<IRecord_CRMNote_Core, IRecord_CRMNote>(`/crm/contacts/${payload.org}/${payload.id}/notes`, payload.data)
  const newContact = await handlers.get<IRecord_CRMContact_WithData>(`/crm/contacts/${payload.org}/${payload.id}/data`)
  dispatch(actions.setContactDetailNotes(newContact))
  dispatch(snackbarActions.setSuccess(`note created`))
  return true
}

const updateNote: ThunkHandler<UpdatePayloadIdOrgEntity<IRecord_CRMNote_Core>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put<IRecord_CRMNote_Core, IRecord_CRMNote>(`/crm/contacts/${payload.org}/${payload.id}/notes/${payload.entity}`, payload.data)
  const newContact = await handlers.get<IRecord_CRMContact_WithData>(`/crm/contacts/${payload.org}/${payload.id}/data`)
  dispatch(actions.setContactDetailNotes(newContact))
  dispatch(snackbarActions.setSuccess(`note updated`))
  return true
}

const deleteNote: ThunkHandler<UpdatePayloadIdOrgEntity, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete<IRecord_CRMNote>(`/crm/contacts/${payload.org}/${payload.id}/notes/${payload.entity}`)
  const newContact = await handlers.get<IRecord_CRMContact_WithData>(`/crm/contacts/${payload.org}/${payload.id}/data`)
  dispatch(actions.setContactDetailNotes(newContact))
  dispatch(snackbarActions.setSuccess(`note deleted`))
  return true
}

/*

  events

*/
const createEvent: ThunkHandler<UpdatePayloadIdOrg<IRecord_CRMEvent_Core & IData_CRMEvent_Notes>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.post<IRecord_CRMEvent_Core & IData_CRMEvent_Notes, IRecord_CRMEvent>(`/crm/contacts/${payload.org}/${payload.id}/events`, payload.data)
  const newContact = await handlers.get<IRecord_CRMContact_WithData>(`/crm/contacts/${payload.org}/${payload.id}/data`)
  dispatch(actions.setContactDetailEvents(newContact))
  dispatch(actions.setContactDetailNotes(newContact))
  dispatch(snackbarActions.setSuccess(`event created`))
  return true
}

const updateEvent: ThunkHandler<UpdatePayloadIdOrgEntity<Partial<IRecord_CRMEvent_Core>>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put<Partial<IRecord_CRMEvent_Core>, IRecord_CRMEvent>(`/crm/contacts/${payload.org}/${payload.id}/events/${payload.entity}`, payload.data)
  const newContact = await handlers.get<IRecord_CRMContact_WithData>(`/crm/contacts/${payload.org}/${payload.id}/data`)
  dispatch(actions.setContactDetailEvents(newContact))
  dispatch(actions.setContactDetailNotes(newContact))
  dispatch(snackbarActions.setSuccess(`event updated`))
  return true
}

const deleteEvent: ThunkHandler<UpdatePayloadIdOrgEntity, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete<IRecord_CRMEvent>(`/crm/contacts/${payload.org}/${payload.id}/events/${payload.entity}`)
  const newContact = await handlers.get<IRecord_CRMContact_WithData>(`/crm/contacts/${payload.org}/${payload.id}/data`)
  dispatch(actions.setContactDetailEvents(newContact))
  dispatch(actions.setContactDetailNotes(newContact))
  dispatch(snackbarActions.setSuccess(`event deleted`))
  return true
}

/*

  magic tags

*/
const loadMagicTags: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_MagicTag_Summary[]>(`/crm/magic_tag_summary`)
  dispatch(actions.setMagicTags(data))
}

/*

  query summary

*/
const loadQuerySummary: ThunkHandler<IQuery_CRM_Summary, void> = (query) => async (dispatch, getState) => {
  const summary = projectSelectors.summary(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.put<IQuery_CRM_Summary, IData_CRM_Summary[]>(`/crm/query_summary/${summary?.org}`, query)
  dispatch(actions.setQuerySummary(data))
}


/*

  collections

*/
const reducers = {
  setTags,
  setImports,
  setSearchResults,
  setEventTypes,
  setEventGroups,
  setEventData,
  setContactData,
  resetContactData,
  setContactDetail,
  setContactDetailNotes,
  setContactDetailEvents,
  setMagicTags,
  setQuerySummary,
}

export const actions = {

  /*
  
    reducers
  
  */
  setTags: CreateAction(prefix, `setTags`, reducers.setTags),
  setImports: CreateAction(prefix, `setImports`, reducers.setImports),
  setSearchResults: CreateAction(prefix, `setSearchResults`, reducers.setSearchResults),
  setEventTypes: CreateAction(prefix, `setEventTypes`, reducers.setEventTypes),
  setEventGroups: CreateAction(prefix, `setEventGroups`, reducers.setEventGroups),
  setEventData: CreateAction(prefix, `setEventData`, reducers.setEventData),
  setContactData: CreateAction(prefix, `setContactData`, reducers.setContactData),
  resetContactData: CreateAction(prefix, `resetContactData`, reducers.resetContactData),
  setContactDetail: CreateAction(prefix, `setContactDetail`, reducers.setContactDetail),
  setContactDetailNotes: CreateAction(prefix, `setContactDetailNotes`, reducers.setContactDetailNotes),
  setContactDetailEvents: CreateAction(prefix, `setContactDetailEvents`, reducers.setContactDetailEvents),
  setMagicTags: CreateAction(prefix, `setMagicTags`, reducers.setMagicTags),
  setQuerySummary: CreateAction(prefix, `setQuerySummary`, reducers.setQuerySummary),

  /*
  
    search
  
  */
  search: NetworkThunk(prefix, 'search', search, {
    snackbar: true,
  }),

  /*
  
    tags
  
  */
  
  listTags: NetworkThunk(prefix, 'listTags', listTags, {
    snackbar: true,
  }),
  createTag: NetworkThunk(prefix, 'tagForm', createTag, {
    snackbar: true,
    globalLoading: {
      message: 'creating tag...'
    },
  }),
  updateTag: NetworkThunk(prefix, 'tagForm', updateTag, {
    snackbar: true,
    globalLoading: {
      message: 'upating tag...'
    },
  }),
  deleteTag: NetworkThunk(prefix, 'deleteTag', deleteTag, {
    snackbar: true,
    globalLoading: {
      message: 'deleting tag...'
    },
  }),

  /*
  
    imports
  
  */

  listImports: NetworkThunk(prefix, 'listImports', listImports, {
    snackbar: true,
  }),
  createImport: NetworkThunk(prefix, 'createImport', createImport, {
    snackbar: true,
    globalLoading: {
      message: 'creating import...'
    },
  }),
  processImport: NetworkThunk(prefix, 'processImport', processImport, {
    snackbar: true,
    globalLoading: {
      message: 'processing import...'
    },
  }),
  runImport: NetworkThunk(prefix, 'runImport', runImport, {
    snackbar: true,
    globalLoading: {
      message: 'running import...'
    },
  }),

  subscribeToImportUpdates: NetworkThunk(prefix, 'subscribeToImportUpdates', subscribeToImportUpdates, {
    snackbar: true,
  }),
  unsubscribeToImportUpdates: NetworkThunk(prefix, 'unsubscribeToImportUpdates', unsubscribeToImportUpdates, {
    snackbar: true,
  }),

  /*

    events
  
  */
  loadEventTypes: NetworkThunk(prefix, 'loadEventTypes', loadEventTypes, {
    snackbar: true,
  }),

  loadEventGroups: NetworkThunk(prefix, 'loadEventGroups', loadEventGroups, {
    snackbar: true,
  }),

  loadEvents: NetworkThunk(prefix, 'loadEvents', loadEvents, {
    snackbar: true,
  }),

  createEvent: NetworkThunk(prefix, 'eventForm', createEvent, {
    snackbar: true,
    globalLoading: {
      message: 'creating event...'
    },
  }),

  updateEvent: NetworkThunk(prefix, 'eventForm', updateEvent, {
    snackbar: true,
    globalLoading: {
      message: 'updating event...'
    },
  }),

  deleteEvent: NetworkThunk(prefix, 'deleteEvent', deleteEvent, {
    snackbar: true,
    globalLoading: {
      message: 'deleting event...'
    },
  }),

  /*

    contacts
  
  */
  loadContacts: NetworkThunk(prefix, 'loadContacts', loadContacts, {
    snackbar: true,
  }),

  getContact: NetworkThunk(prefix, 'getContact', getContact, {
    snackbar: true,
  }),

  createContact: NetworkThunk(prefix, 'contactForm', createContact, {
    snackbar: true,
    globalLoading: {
      message: 'creating contact...'
    },
  }),

  updateContact: NetworkThunk(prefix, 'contactForm', updateContact, {
    snackbar: true,
    globalLoading: {
      message: 'updating contact...'
    },
  }),

  deleteContact: NetworkThunk(prefix, 'deleteContact', deleteContact, {
    snackbar: true,
    globalLoading: {
      message: 'deleting contact...'
    },
  }),

  /*

    notes
  
  */
  createNote: NetworkThunk(prefix, 'noteForm', createNote, {
    snackbar: true,
    globalLoading: {
      message: 'creating note...'
    },
  }),

  updateNote: NetworkThunk(prefix, 'noteForm', updateNote, {
    snackbar: true,
    globalLoading: {
      message: 'updating note...'
    },
  }),

  deleteNote: NetworkThunk(prefix, 'deleteNote', deleteNote, {
    snackbar: true,
    globalLoading: {
      message: 'deleting note...'
    },
  }),

  /*

    magic tags
  
  */
  loadMagicTags: NetworkThunk(prefix, 'loadMagicTags', loadMagicTags, {
    snackbar: true,
  }),

  /*
  
    query summary
  
  */
  loadQuerySummary: NetworkThunk(prefix, 'loadQuerySummary', loadQuerySummary, {
    snackbar: true,
  }),
}

export const reducer = CreateReducer<CRMState>({
  prefix,
  initialState,
  reducers,
})

export default actions